/** */
import React, { useContext, useEffect, useState } from 'react';
import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import { setPageTitle } from 'store/actions/header';
import { useDispatch, useSelector } from 'react-redux';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { PlanCard, WalletCard } from './sections/plan';
import { Spacer } from 'app/layouts/generic';
import { Button } from 'app/shared/button';
import { OnboardingCallModalContext } from 'contexts/onboarding/call-modal';
import { ELIGIBLE_TIERS } from 'app/shared/onboarding/call-modal';
import { useSubscriptionService } from 'hooks/sales/subscription';
import useScreenSize from 'hooks/size';
import { StatsBar } from './sections/stats';
import { CampaignPerformanceChart } from './sections/charts/campaign';
import { CampaignActivity } from './sections/activity/campaign';
import { AudienceHealthRings } from './sections/activity/audience-health';
import { useChecklist } from 'hooks/checklist';
import { useAIReportService } from 'hooks/ai/report';
import { SetupChecklist } from './sections/checklist';
import { PulseLine } from './sections/pulse';
import { AnnouncementCard } from './sections/announcement';
import { ConversionTrackingPanel } from './sections/conversion';
import { useTenant } from 'contexts/tenant';
import { UpsellBanner } from 'app/shared/upsell/banner';
import styles from './dashboard.module.css';

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
};

const GMDashboard = () => {
  const dispatch = useDispatch();
  const { id: user_id, firstname } = useSelector((state) => state.user_data);
  const { tenant } = useTenant();
  const is_creator = tenant?.id ? tenant.creator_id === user_id : null;
  const { isMobile } = useScreenSize();
  const { openModal: openOnboardingCallModal } = useContext(OnboardingCallModalContext);
  const [pulse_line, setPulseLine] = useState('');
  const [pulse_loading, setPulseLoading] = useState(true);
  const [is_call_eligible, setIsCallEligible] = useState(false);
  const { fetchPulseLine } = useAIReportService();
  const { fetchSubscription } = useSubscriptionService();
  const {
    steps,
    completed_count,
    total_count,
    progress_pct,
    header_message,
    show_checklist,
    loading,
    refresh
  } = useChecklist();

  useEffect(() => {
    dispatch(
      setPageTitle([
        { title: '', path: '' },
        { title: '', path: '' }
      ])
    );
    refresh();
    fetchPulseLine()
      .then((payload) => {
        if (payload?.pulse_line) setPulseLine(payload.pulse_line);
      })
      .catch(() => {})
      .finally(() => setPulseLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    fetchSubscription({ query_string: 'plan_code=campaign&status=active' }).then(
      ({ subscriptions: [subscription] = [] }) => {
        setIsCallEligible(ELIGIBLE_TIERS.includes(subscription?.tier));
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageContentWrapper className={styles.wrapper}>
      {isMobile && <Spacer multiple={2} />}
      {firstname && (
        <>
          <p className={styles.salutation}>
            Good {getGreeting()}, {firstname}
          </p>
          <PulseLine text={pulse_line} loading={pulse_loading} />
          <Spacer multiple={4} />
          {is_creator !== null && (!is_creator || (!show_checklist && !loading)) && (
            <>
              <UpsellBanner />
              <Spacer multiple={2} />
            </>
          )}
        </>
      )}
      {is_creator !== null && (!is_creator || (!show_checklist && !loading)) && (
        <>
          <GridRow num_of_columns={1}>
            <GridColumn>
              <StatsBar />
            </GridColumn>
          </GridRow>
          <Spacer multiple={2} />
        </>
      )}
      <GridRow num_of_columns={5} mobileStyles={{ gap: 0 }}>
        <GridColumn span={3}>
          {is_creator !== null && is_creator && (show_checklist || loading) && (
            <>
              <SetupChecklist
                steps={steps}
                progress_pct={progress_pct}
                completed_count={completed_count}
                total_count={total_count}
                header_message={header_message}
                loading={loading}
                onRefresh={refresh}
              />
              <Spacer multiple={2} />
            </>
          )}
          <CampaignPerformanceChart />
          <Spacer multiple={2} />
          {!isMobile && <CampaignActivity />}
          <Spacer multiple={2} />
          <ConversionTrackingPanel />
        </GridColumn>
        <GridColumn span={2}>
          {is_call_eligible && show_checklist && (
            <>
              <Button
                text="Book onboarding call"
                icon_name="calendar"
                onClick={openOnboardingCallModal}
              />
              <Spacer multiple={2} />
            </>
          )}
          <AnnouncementCard />
          <AudienceHealthRings />
          <Spacer multiple={2} />
          <PlanCard />
          <Spacer multiple={2} />
          <WalletCard />
        </GridColumn>
      </GridRow>

      <Spacer multiple={4} />
    </PageContentWrapper>
  );
};

export default GMDashboard;
